import React, { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { useReducedMotion } from 'framer-motion';
import { Reveal, CanvasGrid } from '../components/ui';
import MorphText from '../components/ui/morph-text';
import { Button } from '../components/ui/Button';
import Carousel from '../components/Carousel';
import ProductCard from '../components/ProductCard';
import { PRODUCTS } from '../data/products';
import { CONTACT_EMAIL, QUOTE_URL, mailto } from '../lib/site';

const textLink =
  'text-primary-300 hover:text-primary-200 underline underline-offset-4 rounded focus:outline-none focus-visible:ring-2 focus-visible:ring-primary-400';

const WHAT_WE_DO: { title: string; desc: string; cta: string; href: string; external?: boolean }[] = [
  {
    title: 'Custom development',
    desc: 'Websites, applications and systems built to your requirements.',
    cta: 'Get a quote',
    href: QUOTE_URL,
    external: true,
  },
  {
    title: 'Our own products',
    desc: 'Software we build and run ourselves, live and in development.',
    cta: 'Our Work',
    href: '/work',
  },
  {
    title: 'Consulting, games and learning',
    desc: 'Technology solutions, business growth, game development and education.',
    cta: 'All services',
    href: '/services',
  },
];

const DESKTOP_DOTS = '(min-width: 1024px)';

const Home: React.FC = () => {
  const prefersReducedMotion = useReducedMotion();
  const [desktop, setDesktop] = useState(
    () => typeof window !== 'undefined' && window.matchMedia(DESKTOP_DOTS).matches,
  );
  const [smooth, setSmooth] = useState({ x: -999, y: -999 });
  const [ripplePoint, setRipplePoint] = useState<{ x: number; y: number; id: number } | null>(null);
  const rawRef = useRef({ x: -999, y: -999 });
  const rippleIdRef = useRef(0);

  useEffect(() => {
    const mq = window.matchMedia(DESKTOP_DOTS);
    const apply = () => setDesktop(mq.matches);
    apply();
    mq.addEventListener('change', apply);
    return () => mq.removeEventListener('change', apply);
  }, []);

  // Pointer-following grid. Desktop only, and state only changes while the pointer is moving.
  useEffect(() => {
    if (!desktop) return;
    const onMove = (e: MouseEvent) => {
      rawRef.current = { x: e.clientX, y: e.clientY };
    };
    window.addEventListener('mousemove', onMove, { passive: true });
    let raf = 0;
    const tick = () => {
      const k = prefersReducedMotion ? 0.12 : 0.2;
      setSmooth((p) => {
        const nx = p.x + (rawRef.current.x - p.x) * k;
        const ny = p.y + (rawRef.current.y - p.y) * k;
        return Math.abs(nx - p.x) < 0.4 && Math.abs(ny - p.y) < 0.4 ? p : { x: nx, y: ny };
      });
      raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => {
      window.removeEventListener('mousemove', onMove);
      cancelAnimationFrame(raf);
    };
  }, [prefersReducedMotion, desktop]);

  const handleHeroPointerDownCapture = (e: React.PointerEvent<HTMLElement>) => {
    if (prefersReducedMotion) return;
    if (e.pointerType === 'mouse' && e.button !== 0) return;
    rippleIdRef.current += 1;
    setRipplePoint({ x: e.clientX, y: e.clientY, id: rippleIdRef.current });
  };

  return (
    <div className="relative">
      {desktop && (
        <div
          className="absolute z-0 left-0 right-0 bottom-0 pointer-events-none overflow-hidden"
          style={{ top: 'calc(-1 * var(--header-height, 3.5rem))' }}
          aria-hidden="true"
        >
          <CanvasGrid mx={smooth.x} my={smooth.y} rippleTrigger={ripplePoint} reducedMotion={!!prefersReducedMotion} />
          <div className="absolute inset-0 home-dot-veil" />
        </div>
      )}
      <div
        className="relative z-10"
        onPointerDownCapture={desktop ? handleHeroPointerDownCapture : undefined}
      >
      {/* Hero: who we are + two next actions */}
      <section
        className="relative flex items-center justify-center"
        style={{
          minHeight: 'min(82svh, 40rem)',
          marginTop: 'calc(-1 * var(--header-height, 3.5rem))',
          paddingTop: 'var(--header-height, 3.5rem)',
        }}
      >
        <div className="relative z-10 text-center page-inner flex flex-col items-center py-8 sm:py-10">
          <img
            src="/FinalLogoRebuild.png"
            alt="FairCorp"
            className="w-28 h-28 sm:w-40 sm:h-40 object-contain drop-shadow-lg mb-3"
            style={{ animation: prefersReducedMotion ? undefined : 'fadeUp 0.6s ease both' }}
          />
          <h1 className="sr-only">FairCorp — software products and commissioned development</h1>
          <div className="mb-2" aria-hidden="true">
            <MorphText
              words={['Innovation.', 'Excellence.', 'Engineering.']}
              interval={2800}
              fontSize="clamp(1.75rem, 6vw, 3rem)"
            />
          </div>
          <p className="text-secondary-300 text-base sm:text-lg max-w-xl mx-auto mb-6 leading-relaxed">
            FairCorp builds its own software products and takes on commissioned websites and applications.
          </p>
          <div className="flex flex-col sm:flex-row items-stretch sm:items-center justify-center gap-3 w-full sm:w-auto">
            <Button size="lg" to="/work">Our Work</Button>
            <Button variant="outline" size="lg" to="/services">Our services</Button>
          </div>
        </div>
      </section>

      {/* 1. Featured products & work */}
      <section className="page-shell py-8 sm:py-12" aria-labelledby="home-work">
        <div className="page-inner">
          <div className="flex items-end justify-between gap-4 mb-4">
            <h2 id="home-work" className="text-2xl sm:text-3xl font-bold tracking-tight">
              <span className="cw">Products </span>
              <span className="cb">&amp; work</span>
            </h2>
            <Link to="/work" className={`text-sm font-semibold shrink-0 ${textLink}`}>Our Work →</Link>
          </div>
          <Carousel label="Featured products">
            {PRODUCTS.map((p) => (
              <ProductCard key={p.id} product={p} />
            ))}
          </Carousel>
        </div>
      </section>

      {/* 2. What we do: short summaries that point at the real destinations */}
      <section className="page-shell pb-8 sm:pb-12" aria-labelledby="home-do">
        <div className="page-inner">
          <Reveal>
            <h2 id="home-do" className="text-2xl sm:text-3xl font-bold tracking-tight mb-3">
              <span className="cw">What we </span>
              <span className="cb">do</span>
            </h2>
            <ul className="rounded-2xl border border-secondary-700/60 bg-secondary-900 divide-y divide-secondary-800/70">
              {WHAT_WE_DO.map((r) => (
                <li key={r.title} className="p-4 sm:px-6 sm:py-5 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-1.5 sm:gap-8">
                  <div className="min-w-0">
                    <h3 className="font-semibold text-white leading-tight">{r.title}</h3>
                    <p className="text-sm text-secondary-400 mt-0.5">{r.desc}</p>
                  </div>
                  {r.external ? (
                    <a href={r.href} className={`shrink-0 inline-flex items-center min-h-[44px] text-sm font-semibold ${textLink}`}>
                      {r.cta} ↗
                    </a>
                  ) : (
                    <Link to={r.href} className={`shrink-0 inline-flex items-center min-h-[44px] text-sm font-semibold ${textLink}`}>
                      {r.cta} →
                    </Link>
                  )}
                </li>
              ))}
            </ul>
          </Reveal>
        </div>
      </section>
      {/* 3. Company + contact */}
      <section className="page-shell pb-10 sm:pb-14" aria-labelledby="home-about">
        <div className="page-inner">
          <Reveal>
            <div className="grid md:grid-cols-2 gap-6 md:gap-10">
              <div>
                <h2 id="home-about" className="text-xl font-bold text-white mb-2">About FairCorp</h2>
                <p className="text-secondary-400 mb-3">
                  Founded in 2024. Based in Broomfield, CO.
                </p>
                <div className="flex flex-wrap gap-x-5 gap-y-1 text-sm font-semibold">
                  <Link to="/about" className={textLink}>Our story</Link>
                </div>
              </div>
              <div>
                <h2 className="text-xl font-bold text-white mb-2">Get in touch</h2>
                <p className="text-secondary-400 mb-2">
                  <a href={mailto()} className={`font-semibold break-all ${textLink}`}>{CONTACT_EMAIL}</a>
                </p>
                <p className="text-sm text-secondary-500">
                  Partnerships, careers or support?{' '}
                  <Link to="/contact" className={textLink}>See all channels</Link>.
                </p>
              </div>
            </div>
          </Reveal>
        </div>
      </section>
      </div>
    </div>
  );
};

export default Home;
